import React, { useState } from "react";
import { http } from "@/lib/api";
import { Sparkles, Loader2, CheckCircle2, Radio, Lightbulb, HelpCircle } from "lucide-react";

const BLOCKS = [
  { key: "fact", label: "FACT", icon: CheckCircle2, color: "text-emerald-400", border: "border-emerald-900/50", desc: "Objectively happening" },
  { key: "signal", label: "SIGNAL", icon: Radio, color: "text-amber-400", border: "border-amber-900/50", desc: "Detected activity" },
  { key: "interpretation", label: "INTERPRETATION", icon: Lightbulb, color: "text-cyan-400", border: "border-cyan-900/50", desc: "What it could mean" },
  { key: "uncertainty", label: "UNCERTAINTY", icon: HelpCircle, color: "text-rose-400", border: "border-rose-900/50", desc: "What is unknown" },
];

export default function AIResearch({ kind, refId, presets = [] }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (question) => {
    setLoading(true);
    setRes(null);
    try {
      const r = await http.post("/ai/research", { kind, ref_id: refId, question: question || q });
      setRes(r.data.data);
    } catch (e) {
      setRes({
        fact: "The AI research service could not be reached.",
        signal: "—",
        interpretation: "—",
        uncertainty: "Please retry shortly.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pnut-card p-5" data-testid="ai-research-panel">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-amber-400" />
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-slate-200">
          AI Research Assistant
        </h3>
        <span className="ml-auto font-mono text-[10px] text-slate-600">GPT-5.4 · evidence-only</span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          data-testid="ai-research-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="Ask a research question…"
          className="flex-1 rounded-lg border border-slate-800 bg-void px-3 py-2 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-amber-600/50"
        />
        <button
          data-testid="ai-research-run"
          onClick={() => run()}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-amber-50 hover:bg-amber-500 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Analyze
        </button>
      </div>

      {presets.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => run(p)}
              className="rounded-full border border-slate-800 bg-slate-900/60 px-2.5 py-1 text-[11px] text-slate-400 hover:border-amber-600/40 hover:text-amber-300"
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Analyzing DEMO evidence…
        </div>
      )}

      {res && !loading && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {BLOCKS.map((b) => (
            <div key={b.key} className={`rounded-lg border bg-void/60 p-3 ${b.border}`}>
              <div className="mb-1.5 flex items-center gap-1.5">
                <b.icon className={`h-3.5 w-3.5 ${b.color}`} />
                <span className={`font-mono text-[11px] font-bold tracking-wider ${b.color}`}>{b.label}</span>
                <span className="ml-auto text-[9px] text-slate-600">{b.desc}</span>
              </div>
              <p className="text-[13px] leading-relaxed text-slate-300">{res[b.key]}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
